import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { VoucherType } from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';

// Interface for paginated response from backend
interface PaginatedVoucherTypeResponse {
  data: VoucherType[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

interface BasicVoucherType {
  id: string;
  name: string;
}

interface BasicVoucherTypeResponse {
  data: BasicVoucherType[];
}

export const useGetVoucherType = () => {
  const [voucherTypes, setVoucherTypes] = useState<VoucherType[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchVoucherTypes = async (
    page = 1,
    limit = 15,
    search = '',
    sortBy = 'displayOrder',
    order = 'ASC'
  ) => {
    setLoading(true);
    try {
      const response =
        await axiosTokenInstance.get<PaginatedVoucherTypeResponse>(
          apiUrls.VOUCHER_TYPE_MANAGEMENT,
          {
            params: {
              offset: page,
              limit: limit,
              search: search,
              sortBy: sortBy,
              order: order,
            },
            withCredentials: true,
          }
        );

      // Extract data from paginated response
      setVoucherTypes(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setVoucherTypes([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deleteVoucherType = async (id: string, companyId: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        apiUrls.DELETE_VOUCHER_TYPE,
        {
          params: { id },
        }
      );

      showSnackbar(response.data.message, 'success');
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    }
  };

  return {
    loading,
    voucherTypes,
    totalItems,
    totalPages,
    currentPage,
    fetchVoucherTypes,
    deleteVoucherType,
  };
};

export const useGetBasicVoucherTypes = () => {
  const [basicVoucherTypes, setBasicVoucherTypes] = useState<
    BasicVoucherType[]
  >([]);
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = StyledSnackbar();

  const fetchBasicVoucherTypes = async () => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<BasicVoucherTypeResponse>(
        apiUrls.VOUCHER_TYPE_LIST_BASIC,
        {
          withCredentials: true,
        }
      );

      setBasicVoucherTypes(response.data.data || []);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setBasicVoucherTypes([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    basicVoucherTypes,
    fetchBasicVoucherTypes,
  };
};
